import { SelectOption } from '@frontend/components/basic-forms';
import { useModals } from '@frontend/modal';
import { TransactionWorkflowClient } from '@frontend/transaction/api';
import { Transaction, TransactionTriggerName } from '@frontend/transaction/types';
import { PossibleTrigger } from '@frontend/workflow/types';
import { useEffect, useState } from 'react';

import { groupPossibleTriggerOptions } from '../../../workflow/possible-trigger-labels';
import { getTransactionPossibleTriggerDisplay } from './transaction-possible-trigger-display';

export interface ChangeTransactionStateModalProps {
    transactions: Transaction[];
    onSubmitCallback?: () => void;
}

export const useChangeTransactionStateModal = (props: ChangeTransactionStateModalProps) => {
    const { closeTopModal } = useModals();
    const [selectedTrigger, setSelectedTrigger] = useState<TransactionTriggerName | undefined>(undefined);
    const [options, setOptions] = useState<SelectOption<TransactionTriggerName>[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        setSelectedTrigger(undefined);
        loadTransactionTriggerOptions(props.transactions)
            .then(setOptions)
            .finally(() => setIsLoading(false));
    }, [props.transactions]);

    return { options, selectedTrigger, setSelectedTrigger, closeTopModal, isLoading };
};

export async function loadTransactionTriggerOptions(transactions: Transaction[]): Promise<SelectOption<TransactionTriggerName>[]> {
    const transaction = transactions[0];
    if (!transaction) return [];

    try {
        const triggers = await TransactionWorkflowClient.fetchTransactionPossibleTriggers(transaction.account_id, transaction.id);
        return mapTransactionTriggerOptions(triggers);
    } catch {
        return [];
    }
}

export function mapTransactionTriggerOptions(triggers: PossibleTrigger<TransactionTriggerName, string | null>[]): SelectOption<TransactionTriggerName>[] {
    return groupPossibleTriggerOptions(
        triggers.map((possibleTrigger) => {
            const { group, display } = getTransactionPossibleTriggerDisplay(possibleTrigger);
            return {
                group,
                option: {
                    display,
                    label: possibleTrigger.trigger,
                    value: possibleTrigger.trigger
                }
            };
        })
    );
}
